import axios from 'axios';

const HOSTAPI = process.env.REACT_APP_BASE_URL || '';

export type CertificateType = 'none' | 'cpe' | 'shrm';

export interface EligibilityResponse {
  certificateEnabled: boolean;
  certificateType: CertificateType;
  eligible: boolean;
  creditValue: number;
  attendedMinutes: number;
  pollCompleted: boolean;
  certificateStatus:
    | 'available'
    | 'pending'
    | 'not_eligible'
    | 'disabled'
    | 'downloaded'
    | 'emailed';
  message: string;
}

export async function issueCertificateToken(
  eventId: number | string,
  email: string,
): Promise<string> {
  const res = await axios.get(
    `${HOSTAPI}/events/${eventId}/certificate/token`,
    { params: { email } },
  );
  return res.data?.token as string;
}

export async function getCertificateEligibility(
  eventId: number | string,
  email: string,
  token: string,
): Promise<EligibilityResponse> {
  const res = await axios.get(
    `${HOSTAPI}/events/${eventId}/certificate/eligibility`,
    { params: { email, token } },
  );
  return res.data as EligibilityResponse;
}

export async function downloadCertificate(
  eventId: number | string,
  email: string,
  token: string,
): Promise<{ url: string }> {
  const res = await axios.post(
    `${HOSTAPI}/events/${eventId}/certificate/download`,
    { email, token },
  );
  return res.data as { url: string };
}

export async function emailCertificate(
  eventId: number | string,
  email: string,
  token: string,
): Promise<void> {
  await axios.post(`${HOSTAPI}/events/${eventId}/certificate/email`, {
    email,
    token,
  });
}

export interface CertificateProgressPollMarker {
  pollId: string;
  sequence: number;
  answered: boolean;
}

export interface CertificateProgressResponse {
  certificateEnabled: boolean;
  certificateType: 'none' | 'cpe' | 'shrm';
  requiredMinutes: number;
  attendedSeconds: number;
  attendedMinutes: number;
  pollsAnswered: number;
  totalPolls: number;
  pollMarkers: CertificateProgressPollMarker[];
  eligible: boolean | null;
  creditValue: number | null;
  status: string | null;
  downloadAvailable: boolean;
  /**
   * Authoritative live-state flag from the backend (derived from event
   * timestamps). When true, the widget should show the "live" phase
   * regardless of the dashboard's per-browser `isLive` prop — fixes the
   * bug where two attendees in the same meeting saw different phases
   * because one browser's snapshot was stale.
   */
  meetingLive?: boolean;
}

export async function getCertificateProgress(
  eventId: number | string,
  email: string,
  token: string,
): Promise<CertificateProgressResponse> {
  const res = await axios.get(
    `${HOSTAPI}/events/${eventId}/certificate/progress`,
    { params: { email, token } },
  );
  return res.data as CertificateProgressResponse;
}

/** Build the native-certificate file URL — backend 302s to a presigned S3 URL. */
export function certificateFileUrl(
  eventId: number | string,
  email: string,
  token: string,
): string {
  const params = new URLSearchParams({ email, token });
  return `${HOSTAPI}/events/${eventId}/certificate/file?${params.toString()}`;
}
